import type { ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from '@/shared/ui/tooltip';

interface PageHeaderProps {
  title: string;
  description?: string;
  /** Texto exibido no tooltip do ícone de ajuda ao lado do título. */
  help?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Oculta descrição e botão de ajuda abaixo de `sm` (desktop permanece). */
  hideMetaOnMobile?: boolean;
  /** Controle à esquerda do título, como a seta de voltar nas subpáginas. */
  leading?: ReactNode;
}

export function PageHeader({
  title,
  description,
  help,
  actions,
  className,
  hideMetaOnMobile = false,
  leading,
}: PageHeaderProps) {
  return (
    <div
      className={cn('mb-6 flex flex-row items-start justify-between gap-3 sm:items-center', className)}
    >
      <div className="flex min-w-0 flex-1 items-start gap-1">
        {leading ? <div className="shrink-0">{leading}</div> : null}
        <div className="min-w-0 flex-1">
          <h1 className="flex min-w-0 items-start gap-2 break-words text-2xl font-semibold tracking-tight sm:items-center">
            <span className="min-w-0 break-words">{title}</span>
            {help ? (
              <Tooltip content={help} side="right" className="max-w-sm text-left leading-relaxed">
                <button
                  type="button"
                  className={cn(
                    'inline-grid size-7 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground',
                    hideMetaOnMobile && 'hidden sm:inline-grid',
                  )}
                  aria-label={`Ajuda: ${title}`}
                >
                  <HelpCircle className="size-4" />
                </button>
              </Tooltip>
            ) : null}
          </h1>
          {description ? (
            <p
              className={cn(
                'mt-1 break-words text-sm text-muted-foreground',
                hideMetaOnMobile && 'hidden sm:block',
              )}
            >
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
